/**
 * Charm-bar builder — drag-and-drop bracelet configurator.
 *
 * UX model:
 *   • User picks one bracelet base (radio) → its variant id + price feed the summary.
 *   • User drags a charm from the palette into a slot on the canvas, OR taps
 *     the charm (mobile fallback) to auto-fill the next empty slot.
 *   • Filled slots have a Remove button; canvas re-flows on removal.
 *   • Selected charm variant IDs + bracelet base variant ID + a shared
 *     _bracelet_build_id line-item property are POSTed to /cart/add.js.
 *
 * Storage: `pv-charm-build` localStorage key persists selection across page
 * reloads so a shopper can browse away and come back.
 */

const STORAGE_KEY = 'pv-charm-build';

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function writeState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
}

function clearState() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function formatMoney(cents, moneyFormat) {
  if (window.Shopify && window.Shopify.formatMoney) {
    return window.Shopify.formatMoney(cents, moneyFormat);
  }
  return '$' + (cents / 100).toFixed(2);
}

function uuid() {
  return 'bb_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

/**
 * Pull a length-in-inches out of a variant title. Handles plain `16"`,
 * compound `Gold / 16"`, `18”`, `16 in` — some necklaces carry colour AND
 * length in one title, where a bare parseFloat() returns NaN and the
 * length picker silently stopped working.
 */
function parseInches(title) {
  const m = String(title || '').match(/(\d+(?:\.\d+)?)\s*(?:"|”|″|in\b)/i);
  return m ? parseFloat(m[1]) : NaN;
}

/**
 * Set a slot's width in container-query units, with a `%` fallback.
 *
 * `cqi` is iOS 16+. On older iPhones assigning a cqi value through the CSSOM
 * is silently discarded, which left slots with NO width at all and made every
 * placed charm invisible. Writing `%` first means that value survives when the
 * cqi assignment is rejected. The numbers are interchangeable: a slot's
 * containing block is the canvas, so 1% of it is exactly 1cqi.
 */
function setSlotWidth(slot, units) {
  const v = Number(units).toFixed(1);
  slot.style.width = v + '%';    // survives on iOS < 16
  slot.style.width = v + 'cqi';  // ignored there, wins everywhere else
}

class CharmBarBuilder extends HTMLElement {
  connectedCallback() {
    // Mode: "bracelet" or "necklace" — toggled by the top-of-page tabs.
    // Slot count + length + charm % all vary per mode; Liquid emits both
    // values as data-{mode}-... attributes and we swap into the primary
    // fields (this.slotCount / this.braceletLenMm / --charm-pct) on switch.
    this.mode = this.dataset.mode || 'bracelet';
    this.slotCountByMode = {
      bracelet: parseInt(this.dataset.braceletSlotCount || this.dataset.slotCount || '5', 10),
      necklace: parseInt(this.dataset.necklaceSlotCount || this.dataset.slotCount || '5', 10),
    };
    this.lengthByMode = {
      bracelet: parseFloat(this.dataset.braceletLengthMm) || 200,
      necklace: parseFloat(this.dataset.necklaceLengthMm) || 450,
    };
    this.charmPctByMode = {
      bracelet: parseFloat(this.dataset.braceletCharmPct) || parseFloat(this.dataset.charmSizePct) || 4.5,
      necklace: parseFloat(this.dataset.necklaceCharmPct) || parseFloat(this.dataset.charmSizePct) || 2,
    };
    this.slotCount = this.slotCountByMode[this.mode];
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    // Physical scale reference — used to size each placed charm to its
    // real-world dimension. If a charm has a width_mm metafield, we
    // compute (charm.widthMm / braceletLenMm) × canvas width for slot
    // sizing. Otherwise the CSS default (--charm-pct) applies.
    this.braceletLenMm = this.lengthByMode[this.mode];
    this.defaultCharmMm = parseFloat(this.dataset.defaultCharmMm) || 9;

    // Necklace charms hang along the bottom curve, not up towards the clasp.
    // `necklaceBottomPct` is how much of the traced vertical span, measured up
    // from the lowest hole, still gets a slot.
    this.necklaceBottomOnly = this.dataset.necklaceBottomOnly !== 'false';
    this.necklaceBottomPct = parseFloat(this.dataset.necklaceBottomPct) || 55;
    // Solid-weave chains: charms slide to the lowest point and bunch, so they
    // get a tighter band and a lower ceiling than a linked chain.
    this.dangleBottomPct = parseFloat(this.dataset.dangleBottomPct) || 40;
    this.dangleMaxSlots = parseInt(this.dataset.dangleMaxSlots || '3', 10);

    // Cache DOM references (v2 layout: slots live in [data-slots] inside [data-canvas])
    this.modeButtons = Array.from(this.querySelectorAll('[data-mode-btn]'));
    this.baseGroups = Array.from(this.querySelectorAll('[data-mode-group]'));
    // baseInputs = only radios in the ACTIVE mode group (enabled ones).
    this.baseInputs = this.getActiveModeInputs();
    this.canvas = this.querySelector('[data-canvas]');
    this.bgImg = this.querySelector('[data-bracelet-image]');
    this.chainFallback = this.querySelector('[data-chain-fallback]');
    this.slotsList = this.querySelector('[data-slots]');
    this.slots = Array.from(this.querySelectorAll('[data-slot]'));
    this.palette = this.querySelector('[data-palette]');
    this.charmButtons = Array.from(this.querySelectorAll('[data-charm]'));
    this.slotSummary = this.querySelector('[data-slot-summary]');
    // Extra chain length, in steps of `stepInches`. See buildExtraLength().
    // Uniform readability boost on charm size. Megan: the charms look too
    // small against the chains — the chains are drawn at their real size and
    // should stay there, the charms just need to read. Uniform, so RELATIVE
    // sizes stay exact: an 18mm charm is still twice a 9mm one.
    this.charmBoost = parseFloat(this.dataset.charmBoost) || 1;
    // Spots the shopper has deliberately passed over. Auto-place walks the
    // spots in Megan's fill order, so without this you can only build outward
    // from the centre — there was no way to leave a gap.
    this.skippedSlots = new Set();
    this.extraSteps = 0;
    this.extraWrap = this.querySelector('[data-extra-length]');
    this.totalBaseEl = this.querySelector('[data-total-base]');
    this.totalCharmsEl = this.querySelector('[data-total-charms]');
    this.totalGrandEl = this.querySelector('[data-total-grand]');
    this.countEl = this.querySelector('[data-count]');
    this.addBtn = this.querySelector('[data-add-to-cart]');
    this.statusEl = this.querySelector('[data-status]');

    // selectedCharms[i] = null | { variantId, productId, title, price, image } for slot i
    this.selectedCharms = new Array(this.slotCount).fill(null);
    this.selectedBase = null;  // { variantId, title, price, image }
    this._detectSeq = 0;       // guards stale auto-detect results on fast base swaps

    // Wire base radios — bind EVERY mode's radios, not just the active one.
    // Megan: "the necklaces won't switch on the necklace side" — the necklace
    // radios exist in the DOM from the start but were never given a change
    // listener, because baseInputs only holds the active group at load.
    this.querySelectorAll('[data-base-input]').forEach(inp => {
      if (inp._cbBound) return;
      inp._cbBound = true;
      inp.addEventListener('change', () => this.onBaseChange(inp));
    });
    const initialBase = Array.from(this.baseInputs).find(i => i.checked) || this.baseInputs[0];
    if (initialBase) this.onBaseChange(initialBase);

    // Wire palette drag + tap.
    //
    // Interaction model (works identically on desktop mouse + mobile touch):
    //   1. Click/tap a charm in the palette → it becomes STAGED (highlighted).
    //   2. Click/tap any slot on the bracelet → staged charm drops there.
    //   3. Click the same staged charm again → auto-fill first empty slot
    //      (quick add for repeat use).
    //   4. Click outside the builder, or press Escape → clear staging.
    //
    // Desktop drag & drop still works exactly as before; staging is
    // additive. Fixes the mobile bug where tapping teleported the charm
    // to the first empty slot regardless of intent.
    this.stagedCharm = null;
    this.rewireCharms();

    // Wire slots — CSS custom props for ellipse math, drag/drop, keyboard
    // remove, AND tap-to-drop-staged-charm.
    const slotCount = this.slots.length;
    this.slots.forEach((slot, i) => {
      slot.style.setProperty('--slot-idx', String(i));
      slot.style.setProperty('--slot-count', String(slotCount));
      slot.addEventListener('dragover', (e) => { e.preventDefault(); slot.classList.add('is-drag-over'); });
      slot.addEventListener('dragleave', () => slot.classList.remove('is-drag-over'));
      slot.addEventListener('drop', (e) => { e.preventDefault(); slot.classList.remove('is-drag-over'); this.onDrop(e, slot); });
      slot.addEventListener('click', (e) => this.onSlotClick(e, slot));
      slot.addEventListener('keydown', (e) => {
        if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedCharms[parseInt(slot.dataset.slot, 10)]) {
          this.removeSlot(parseInt(slot.dataset.slot, 10));
        }
      });
    });

    // Canvas-level drag routing: highlight + accept drops for the NEAREST
    // hole to the pointer (see nearestSlot) so aiming feels exact even in
    // the gaps between slot boxes. Runs after the per-slot handlers, so
    // its nearest-only highlight is the one that sticks each frame.
    if (this.canvas) {
      this.canvas.addEventListener('dragover', (e) => {
        e.preventDefault();
        const near = this.nearestSlot(e.clientX, e.clientY);
        this.slots.forEach(s => s.classList.toggle('is-drag-over', s === near));
      });
      this.canvas.addEventListener('drop', (e) => {
        e.preventDefault();
        this.slots.forEach(s => s.classList.remove('is-drag-over'));
        this.onDrop(e, null);
      });
      this.canvas.addEventListener('dragleave', (e) => {
        if (!this.canvas.contains(e.relatedTarget)) {
          this.slots.forEach(s => s.classList.remove('is-drag-over'));
        }
      });
      // Per-pixel front-charm selection while moving over stacked charms.
      this.canvas.addEventListener('pointermove', (e) => this.updateAlphaHover(e), { passive: true });
      this.canvas.addEventListener('pointerleave', () => {
        this.slots.forEach(s => s.classList.remove('is-front'));
      });
    }

    // Preview: drop the editing furniture so the piece can be seen as worn.
    const pv = this.querySelector('[data-preview-toggle]');
    if (pv && this.canvas) {
      pv.addEventListener('click', () => {
        const on = !this.canvas.classList.contains('is-preview');
        this.canvas.classList.toggle('is-preview', on);
        pv.setAttribute('aria-pressed', String(on));
        const label = pv.querySelector('[data-preview-label]');
        if (label) label.textContent = on ? 'Keep editing' : 'Preview';
        // Staging a charm while the spots are hidden would have nowhere to go.
        if (on && this.stagedCharm) this.clearStagedCharm();
      });
    }

    const clr = this.querySelector('[data-clear-all]');
    if (clr) clr.addEventListener('click', () => this.clearAllCharms());
    const skp = this.querySelector('[data-skip-charm]');
    if (skp) skp.addEventListener('click', () => this.skipNextSpot());

    // Cancel staging on Escape or click outside the builder
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.stagedCharm) this.clearStagedCharm();
    });
    document.addEventListener('click', (e) => {
      if (this.stagedCharm && !this.contains(e.target)) this.clearStagedCharm();
    });

    // Restore prior selection (if same bracelet base is still an option)
    const stored = readState();
    if (stored && stored.baseVariantId) {
      // Stored id may be ANY length variant of a base, not just the first.
      const matchingBase = Array.from(this.baseInputs).find(i => {
        if (i.value === String(stored.baseVariantId)) return true;
        try {
          return JSON.parse(i.dataset.variants || '[]')
            .some(v => String(v.id) === String(stored.baseVariantId));
        } catch { return false; }
      });
      if (matchingBase) {
        matchingBase.checked = true;
        this.onBaseChange(matchingBase);
        const sv = (this.selectedBase?.variants || [])
          .find(v => String(v.id) === String(stored.baseVariantId));
        if (sv) this.applySizeChoice(sv);
      }
      if (Array.isArray(stored.charms)) {
        stored.charms.forEach((c, i) => {
          if (c && i < this.slotCount) this.setSlot(i, c);
        });
      }
    }

    this.addBtn.addEventListener('click', () => this.addToCart());

    // Wire mode toggle buttons (Bracelet ↔ Necklace).
    this.modeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.setMode(btn.dataset.modeBtn));
    });

    // Reflect initial mode's copy in the DOM.
    this.applyModeCopy();

    this.render();
  }

  /**
   * Returns the radios in the currently-active mode group. Radios in
   * the inactive group are `disabled` in Liquid; when we swap modes we
   * flip that so only one group's radios can be `.checked` at a time.
   */
  getActiveModeInputs() {
    const nodes = this.querySelectorAll(`[data-mode-group="${this.mode}"] [data-base-input]`);
    return nodes.length ? nodes : this.querySelectorAll('[data-base-input]');
  }

  /**
   * Switch base type. Clears any in-progress charm placement (slot layout
   * changes because length_mm and slotCount differ per mode). Restores the
   * default-first radio of the target group.
   */
  setMode(nextMode) {
    if (nextMode !== 'bracelet' && nextMode !== 'necklace') return;
    if (nextMode === this.mode) return;

    // Toggle grid visibility + radio enablement so only one mode is active.
    this.baseGroups.forEach(g => {
      const isTarget = g.dataset.modeGroup === nextMode;
      g.hidden = !isTarget;
      g.querySelectorAll('[data-base-input]').forEach(inp => {
        inp.disabled = !isTarget;
        if (!isTarget) inp.checked = false;
      });
    });

    // Toggle button visual state
    this.modeButtons.forEach(b => {
      const isTarget = b.dataset.modeBtn === nextMode;
      b.classList.toggle('is-active', isTarget);
      b.setAttribute('aria-selected', isTarget ? 'true' : 'false');
    });

    // Rebind mode-scoped state.
    this.mode = nextMode;
    this.slotCount = this.slotCountByMode[nextMode];
    this.braceletLenMm = this.lengthByMode[nextMode];
    const newPct = this.charmPctByMode[nextMode];
    this.style.setProperty('--charm-pct', newPct + '%');

    // Reset selection.
    this.selectedCharms = new Array(this.slotCount).fill(null);
    this.selectedBase = null;
    clearState();

    // Refresh active-mode inputs, ensure DOM slot count matches, then pick first radio.
    this.baseInputs = this.getActiveModeInputs();
    this.ensureSlotCount(this.slotCount);
    this.applyModeCopy();

    const first = this.baseInputs[0];
    if (first) {
      first.checked = true;
      this.onBaseChange(first);   // triggers applySlotLayout + render + persist
    } else {
      this.render();
    }
  }

  /**
   * Grow or shrink the pre-rendered slot DOM to match the target count.
   * applySlotLayout() already grows the pool on demand (for hole-count > slotCount);
   * this shrinks when necessary and normalizes visibility so a fresh mode
   * doesn't inherit hidden slots from a prior selection with fewer holes.
   */
  ensureSlotCount(n) {
    if (!this.slotsList) return;
    while (this.slots.length < n) {
      const clone = this.slots[0].cloneNode(false);
      clone.dataset.slot = String(this.slots.length);
      clone.setAttribute('aria-label', `Slot ${this.slots.length + 1} — empty`);
      clone.addEventListener('dragover', (e) => { e.preventDefault(); clone.classList.add('is-drag-over'); });
      clone.addEventListener('dragleave', () => clone.classList.remove('is-drag-over'));
      clone.addEventListener('drop', (e) => { e.preventDefault(); clone.classList.remove('is-drag-over'); this.onDrop(e, clone); });
      clone.addEventListener('click', (e) => this.onSlotClick(e, clone));
      clone.addEventListener('keydown', (e) => {
        if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedCharms[parseInt(clone.dataset.slot, 10)]) {
          this.removeSlot(parseInt(clone.dataset.slot, 10));
        }
      });
      this.slotsList.appendChild(clone);
      this.slots.push(clone);
    }
    // Un-hide the first n; hide the rest (in case a prior base hid them).
    this.slots.forEach((slot, i) => {
      slot.hidden = i >= n;
      slot.style.setProperty('--slot-count', String(n));
      slot.style.setProperty('--slot-idx', String(i));
    });
  }

  /**
   * Update the mode-word / length / max-slots placeholders scattered
   * throughout the section (spec line, step headings, cart button, etc.).
   */
  applyModeCopy() {
    const word = this.mode;                    // "bracelet" | "necklace"
    const wordCap = word[0].toUpperCase() + word.slice(1);
    const len = this.lengthByMode[this.mode];
    const pct = this.charmPctByMode[this.mode];
    const slots = this.slotCountByMode[this.mode];

    this.querySelectorAll('[data-mode-word]').forEach(el => { el.textContent = word; });
    this.querySelectorAll('[data-mode-word-cap]').forEach(el => { el.textContent = wordCap; });
    this.querySelectorAll('[data-mode-length]').forEach(el => { el.textContent = String(len); });
    this.querySelectorAll('[data-scale-pct]').forEach(el => { el.textContent = String(pct); });
    // Only the MODE DEFAULT is known here. Once a base is picked, the traced
    // hole count is the truth, and writing the default over it is what left
    // "Add up to 18 charms" sitting above a counter reading "0 of 9" on the
    // same chain. applySlotLayout() sets activeHoles when it has a real count.
    const advertised = (this.activeHoles && this.activeHoles.length)
      ? this.activeHoles.length
      : slots;
    this.querySelectorAll('[data-max-slots], [data-max-slots-inline]').forEach(el => { el.textContent = String(advertised); });

    const lenLabel = this.querySelector('[data-length-label]');
    if (lenLabel) {
      const inches = (len / 25.4).toFixed(1);
      lenLabel.textContent = `${wordCap} length: ${len} mm (${inches}")`;
    }
  }

  onBaseChange(input) {
    // Parse hole positions (raw traced clicks). When present, this
    // overrides the ellipse math — slots are placed at exact percentages.
    let holes = null;
    const rawHoles = input.dataset.chainHoles;
    if (rawHoles && rawHoles !== '') {
      try {
        const parsed = JSON.parse(rawHoles);
        if (Array.isArray(parsed) && parsed.length > 0 &&
            parsed.every(p => Array.isArray(p) && p.length === 2)) {
          holes = parsed;
        }
      } catch { /* fall back to ellipse */ }
    }

    this.selectedBase = {
      variantId: input.value,
      title: input.dataset.title,
      price: parseInt(input.dataset.price, 10),
      image: input.dataset.image,
      chainShape: input.dataset.chainShape || 'oval',
      chainRx: parseFloat(input.dataset.chainRx) || 42,
      chainRy: parseFloat(input.dataset.chainRy) || 38,
      chainCy: parseFloat(input.dataset.chainCy) || 0,
      chainGap: parseFloat(input.dataset.chainGap) || 40,
      chainHoles: holes,
      // Solid weave (herringbone/wheat/curb): no open links, so charms thread on
      // and slide to the lowest point rather than staying where they're placed.
      dangleOnly: input.dataset.dangleOnly === 'true',
      // Measured chain path length in canvas-% units. See render().
      loopCqi: parseFloat(input.dataset.loopCqi) || null,
      variants: [],
    };
    // Length variants (per Megan: bracelets 6"/7", necklaces 16"/18").
    // The shopper's pick drives the cart variant AND the mm↔screen scale.
    try { this.selectedBase.variants = JSON.parse(input.dataset.variants || '[]'); } catch {}
    const pref = this._sizePref && this._sizePref[this.mode];
    const chosen = this.selectedBase.variants.find(v => v.title === pref)
      || this.selectedBase.variants[0];
    if (chosen) this.applySizeChoice(chosen, false);
    this.buildSizePicker();
    // rerender:false above skips the rebuild, so the extra-length pills and
    // their note would still be describing the PREVIOUS chain — a 16"
    // necklace inherited the bracelet's "Standard 6" length".
    this.buildExtraLength();
    // Swap the bracelet backdrop image + trigger the "drop-in" animation.
    if (this.bgImg) {
      if (this.selectedBase.image) {
        this.bgImg.src = this.selectedBase.image;
        this.bgImg.alt = this.selectedBase.title;
      } else {
        this.bgImg.removeAttribute('src');
        this.bgImg.alt = '';
      }
      const bg = this.bgImg.parentElement;
      if (bg) {
        bg.classList.remove('is-just-loaded');
        void bg.offsetWidth;
        bg.classList.add('is-just-loaded');
      }
    }
    // Apply per-bracelet chain-shape CSS vars so the slot ring matches
    // this specific photo's chain oval. Ignored if hole positions exist.
    this.style.setProperty('--chain-rx',  this.selectedBase.chainRx + 'cqi');
    this.style.setProperty('--chain-ry',  this.selectedBase.chainRy + 'cqi');
    this.style.setProperty('--chain-cy',  this.selectedBase.chainCy + 'cqi');
    this.style.setProperty('--chain-gap', this.selectedBase.chainGap + 'deg');
    this.applySlotLayout();
    this.render();
    this.persist();
    // No traced hole positions on this product? Detect them from the photo
    // right here in the browser — slots snap onto the actual chain instead
    // of the generic ellipse. (Metafield positions, when present, win.)
    if (!this.selectedBase.chainHoles) this.autoDetectHoles(this.selectedBase);
  }

  /**
   * Combined display zoom = frame-fit zoom × chosen-length factor. The
   * LONGEST variant fills the frame; picking the shorter one renders the
   * whole chain proportionally smaller — the CHAIN changes size with the
   * choice, while charms keep the same on-screen mm size (both facts are
   * physically consistent: same charm, different chain).
   */
  updateZoom() {
    const z = (this._fitZoom || 1) * (this._sizeFactor || 1);
    this.style.setProperty('--cb-zoom', z.toFixed(3));
    this.applyCrop(z);
  }

  /**
   * Show a necklace from the bottom half only.
   *
   * Megan asked for this twice — "every other charm bar website has them as
   * half", "people don't need to see the whole chain", "it makes it less
   * awkward to place them". The clasp and the two long side runs carry no
   * charms, so they are wasted frame.
   *
   * The crop is a pure viewport trick. The 3:4 canvas, the traced hole
   * percentages and the mm-to-pixel scale are all left exactly as they were;
   * this only decides how much of that canvas the shopper sees. Because
   * --cb-zoom has already scaled the canvas contents about a fixed origin,
   * the band is converted through that same transform before it is used.
   */
  applyCrop(zoom) {
    const view = this.querySelector('[data-viewport]');
    if (!view) return;
    const band = this._cropBand;
    // Bracelets, untraced chains, and the merchant tracer all want the whole frame.
    // ?trace=1 is the merchant hole tracer — it needs to see the whole photo,
    // and the flag is read from the URL because the overlay may not be in the
    // DOM yet when the first layout runs.
    const tracing = this.querySelector('.cb-tracer')
      || new URLSearchParams(location.search).get('trace') === '1';
    if (!band || this.mode !== 'necklace' || !this.necklaceBottomOnly || tracing) {
      view.classList.remove('is-cropped');
      view.style.removeProperty('--cb-view-ar');
      view.style.removeProperty('--cb-crop-top');
      view.style.removeProperty('--cb-visible');
      return;
    }
    const z = zoom || 1;
    const oy = this._zoomOriginY ?? 50;
    const toScreen = y => oy + (y - oy) * z;
    const bottom = Math.max(0, Math.min(100, toScreen(band.bottom)));
    let top = Math.max(0, Math.min(100, toScreen(band.top)));

    // On a tightly-clustered weave (snake, rope — three holes almost on top of
    // each other) the band alone is a sliver. Open it upward to a floor rather
    // than giving up on the crop, so every necklace in the picker is framed
    // the same way instead of a couple showing full-length next to the halves.
    const MIN = 0.42;
    if ((bottom - top) / 100 < MIN) top = Math.max(0, bottom - MIN * 100);

    const visible = (bottom - top) / 100;
    // Above ~92% there is nothing worth hiding, so skip the layout shift.
    if (!(visible > 0.2 && visible < 0.92)) {
      view.classList.remove('is-cropped');
      view.style.removeProperty('--cb-view-ar');
      view.style.removeProperty('--cb-crop-top');
      view.style.removeProperty('--cb-visible');
      return;
    }
    view.classList.add('is-cropped');
    view.style.setProperty('--cb-crop-top', top.toFixed(2) + '%');
    view.style.setProperty('--cb-visible', visible.toFixed(4));
    view.style.setProperty('--cb-view-ar', `3 / ${(4 * visible).toFixed(4)}`);
  }

  /**
   * Adopt a length variant: cart id, price, and the real physical length
   * that the true-scale charm math runs on (6"→152mm … 18"→457mm).
   */
  /**
   * "Need it longer?" — extra chain in fixed inch steps at a flat rate each.
   *
   * Megan prices length by the step, not by the finished size, so this is ONE
   * add-on product added N times: 22" off an 18" chain is two steps, 26" is
   * four. One SKU covers every length anyone asks for, and the cart line reads
   * as a quantity.
   *
   * The chosen steps also feed braceletLenMm, so the charm scale stays honest
   * — the same charm really does take up less of a longer chain.
   */
  buildExtraLength() {
    if (!this.extraWrap || !this.selectedBase) return;
    const btns = this.extraWrap.querySelector('.charm-bar__extra-btns');
    const note = this.extraWrap.querySelector('[data-extra-note]');
    if (!btns) return;
    const step = parseFloat(this.extraWrap.dataset.stepInches) || 2;
    const price = parseInt(this.extraWrap.dataset.addonPrice, 10) || 0;
    const maxSteps = parseInt(btns.dataset.maxSteps, 10) || 4;
    // Derive the base length from braceletLenMm, which applySizeChoice has
    // already set, rather than re-reading a variant title. The title lookup
    // could still be holding the previous mode's variant on the first call
    // after a mode switch, so a 16" necklace announced itself as
    // "Standard 6" length" — the bracelet size it had just left.
    // Pin the base length to one of THIS chain's own size options. On the
    // first call after a mode switch the running length can still be the
    // previous mode's, which had a 16" necklace announcing itself as
    // "Standard 6" length" — the bracelet size it had just left.
    const sizeOptions = (this.selectedBase.variants || [])
      .map(v => parseInches(v.title)).filter(n => n > 0);
    let baseIn = Math.round((this.braceletLenMm / 25.4) - (this.extraSteps || 0) * step);
    if (sizeOptions.length && !sizeOptions.includes(baseIn)) baseIn = sizeOptions[0];

    btns.innerHTML = '';
    for (let n = 0; n <= maxSteps; n++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'charm-bar__extra-btn' + (n === this.extraSteps ? ' is-active' : '');
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-checked', n === this.extraSteps ? 'true' : 'false');
      b.textContent = n === 0 ? 'Standard' : '+' + (n * step) + '"';
      b.addEventListener('click', () => {
        this.extraSteps = n;
        const v = this.currentSizeVariant();
        if (v) this.applySizeChoice(v);
      });
      btns.appendChild(b);
    }
    if (note) {
      // selectedBase.variants is still empty on the very first call after a
      // base is picked — it is filled in a moment later. Say nothing rather
      // than quote a length derived from the previous mode's chain.
      note.textContent = !sizeOptions.length ? ''
        : this.extraSteps === 0
        ? (baseIn ? 'Standard ' + baseIn + '" length.' : '')
        : (baseIn ? (baseIn + this.extraSteps * step) + '" total — ' : '')
          + '+' + formatMoney(price * this.extraSteps, this.moneyFormat)
          + ' for the extra ' + (this.extraSteps * step) + '".';
    }
  }

  /**
   * Title of the length variant currently selected, e.g. '18"'.
   *
   * Prefers the title applySizeChoice was actually handed. Looking it up from
   * selectedBase.variantId instead read the PREVIOUS mode's variant on the
   * first call after a mode switch, so a 16" necklace announced itself as
   * "Standard 6" length" — the bracelet size it had just left.
   */
  baseSizeTitle() {
    return this._baseSizeTitle || this.currentSizeVariant()?.title || this._sizePref?.[this.mode] || '';
  }
  currentSizeVariant() {
    return (this.selectedBase?.variants || [])
      .find(v => String(v.id) === String(this.selectedBase.variantId)) || null;
  }

  applySizeChoice(v, rerender = true) {
    if (!this.selectedBase) return;
    this._baseSizeTitle = v.title;
    this.selectedBase.variantId = String(v.id);
    this.selectedBase.price = v.price;
    if (!this._sizePref) this._sizePref = {};
    this._sizePref[this.mode] = v.title;
    const step = parseFloat(this.extraWrap?.dataset.stepInches) || 2;
    // The extra inches are real chain, so they belong in the physical length
    // the true-scale charm maths runs on.
    const inch = parseInches(v.title) + (this.extraSteps || 0) * step;
    if (inch > 0) this.braceletLenMm = inch * 25.4;
    // Chain drawn size follows the chosen STOCK length, relative to the longest
    // option — that is the size the frame is fitted to.
    //
    // Deliberately excludes the custom extra inches. Feeding those in made the
    // factor exceed 1 (18" chain at +8" gave 26/18 = 1.44), which multiplies
    // straight into --cb-zoom and blew the photo up 44% inside a frame that
    // does not grow with it: the necklace just zoomed in and ran out of the
    // canvas. Extra length is the SAME chain, longer -- it belongs in
    // braceletLenMm, where it correctly makes each charm a smaller share of the
    // chain, and nowhere near how big the photo is drawn.
    const stockIn = parseInches(v.title);
    const inches = (this.selectedBase.variants || [])
      .map(x => parseInches(x.title)).filter(n => n > 0);
    const maxIn = inches.length ? Math.max(...inches) : stockIn;
    // Clamped at 1 so no length can ever scale the chain past its fitted frame.
    this._sizeFactor = (stockIn > 0 && maxIn > 0) ? Math.min(1, stockIn / maxIn) : 1;
    this.updateZoom();
    const lenLabel = this.querySelector('[data-length-label]');
    if (lenLabel) {
      const word = this.mode[0].toUpperCase() + this.mode.slice(1);
      lenLabel.textContent = `${word} length: ${v.title} (${Math.round(this.braceletLenMm)} mm)`;
    }
    if (rerender) {
      this.buildSizePicker();
      this.buildExtraLength();
      this.render();
      this.persist();
    }
  }

  /** Two pill buttons (6"/7" or 16"/18") under the step-2 heading. */
  buildSizePicker() {
    const wrap = this.querySelector('[data-size-wrap]');
    const el = this.querySelector('[data-size-picker]');
    const cap = this.querySelector('[data-size-caption]');
    if (!el || !wrap) return;
    const vs = this.selectedBase?.variants || [];
    el.innerHTML = '';
    // Section stays visible always — if the base has no size options, we
    // just show a caption prompting the shopper to pick a base first.
    wrap.hidden = false;
    if (vs.length < 2) {
      if (cap) cap.textContent = this.selectedBase
        ? 'This chain only comes in one length.'
        : 'Pick a chain in step 1 to see size options.';
      return;
    }
    let activeInches = null;
    // De-dupe variants by inches — "Gold/16"" and "Silver/16"" collapse to
     // a single 16" pill so the picker only asks about SIZE, never colour.
    // The colour side of the compound variant is chosen elsewhere (or just
     // inherits whatever the base's first-available variant carried).
    const seenInches = new Set();
    const uniq = vs.filter(v => {
      const inch = parseInches(v.title);
      if (!Number.isFinite(inch)) return true;
      if (seenInches.has(inch)) return false;
      seenInches.add(inch);
      return true;
    });
    uniq.forEach(v => {
      const b = document.createElement('button');
      b.type = 'button';
      const isActive = String(v.id) === String(this.selectedBase.variantId);
      b.className = 'charm-bar__size-btn' + (isActive ? ' is-active' : '');
      const priceDiffers = uniq.some(o => o.price !== uniq[0].price);
      const inches = parseInches(v.title);
      const label = Number.isFinite(inches) ? `${inches}"` : v.title;
      b.textContent = label + (priceDiffers ? ' · ' + formatMoney(v.price, this.moneyFormat) : '');
      b.addEventListener('click', () => this.applySizeChoice(v));
      el.appendChild(b);
      if (isActive) activeInches = inches;
    });
    if (cap) {
      if (activeInches && Number.isFinite(activeInches)) {
        cap.innerHTML = `You picked: <strong>${activeInches}"</strong>`;
      } else if (this.selectedBase.variantTitle) {
        cap.innerHTML = `You picked: <strong>${this.selectedBase.variantTitle}</strong>`;
      } else {
        cap.textContent = '';
      }
    }
  }

  /**
   * Client-side fallback when chain.hole_positions isn't set: run the
   * shared photo detection (charm-hole-detect.js) and treat the result
   * exactly like traced holes. Results are cached per image URL in
   * localStorage so repeat selections are instant. Fails-open: any error
   * leaves the ellipse layout in place.
   */
  async autoDetectHoles(base) {
    if (!base.image || !window.CharmHoleDetect) return;
    const cacheKey = 'pv-charm-holes-v1:' + base.image;
    try {
      const cached = JSON.parse(localStorage.getItem(cacheKey));
      if (Array.isArray(cached) && cached.length) {
        this.applyDetectedHoles(base, cached);
        return;
      }
    } catch {}
    const seq = ++this._detectSeq;
    this.setStatus('Placing charm slots on this chain…', '');
    try {
      const n = this.slotCountByMode[this.mode] || this.slotCount;
      const det = await window.CharmHoleDetect.detect(base.image, n);
      if (seq !== this._detectSeq) return;   // user already picked another base
      // Only the canvas ASPECT matters for the mapping; fall back to the
      // CSS-fixed 3:4 if the canvas isn't measurable yet.
      const rect = this.canvas ? this.canvas.getBoundingClientRect() : null;
      const holes = (rect && rect.width && rect.height)
        ? window.CharmHoleDetect.toBoxPct(det, rect.width, rect.height)
        : window.CharmHoleDetect.toBoxPct(det, 3, 4);
      try { localStorage.setItem(cacheKey, JSON.stringify(holes)); } catch {}
      this.applyDetectedHoles(base, holes);
      this.setStatus('', '');
    } catch (e) {
      console.warn('[charm-bar] auto hole-detect failed:', e);
      if (seq === this._detectSeq && this.selectedBase === base) {
        this.setStatus("Couldn't map this chain's photo — try another chain.", 'error');
      }
    }
  }

  applyDetectedHoles(base, holes) {
    base.chainHoles = holes;
    if (this.selectedBase === base) {
      this.applySlotLayout();
      this.render();
    }
  }

  /**
   * When chain.hole_positions is set on the bracelet, place one slot at
   * each hole (exact click positions). Otherwise let the CSS ellipse
   * math handle it via the --slot-idx / --slot-count variables.
   *
   * Uses `top`/`left` percentages + `translate(-50%,-50%)` so each slot
   * sits centred on the hole regardless of the ellipse transform.
   */
  /**
   * Which traced holes actually get a charm slot.
   *
   * On a necklace the charms hang along the bottom curve — nobody wears them
   * climbing up towards the clasp — so in necklace mode we keep only the holes
   * in the lower band of the traced span. Bracelets keep every hole, since a
   * bracelet is worn as a full loop.
   *
   * The FULL trace is still used for the loop-length and zoom maths, so the
   * mm-to-screen scale stays honest; only slot placement is narrowed.
   */
  slotHolesFor(holes) {
    if (!holes || holes.length < 3) return holes;

    // BRACELET: the stored positions ARE the charm spots — use them as they are.
    //
    // These used to be raw link positions, halved here so charms could not
    // land on neighbouring links and collide (Megan: "has 16 holes, so only 8
    // should have charms"). Every bracelet has since been re-published with
    // its final spots already spaced — one per bead gap on the enamel ones,
    // nine evenly round the plain links — after Megan reviewed them chain by
    // chain and marked up what she wanted. Halving again would quietly show
    // her half of what she approved.
    if (this.mode === 'bracelet') return holes;

    // DANGLE CHAINS FIRST. A solid weave — herringbone, snake, rope, twist,
    // satellite — has no open link to clip onto, so a charm threads on and
    // slides to the lowest point wherever it is dropped. They cannot carry a
    // row of eleven; they gather in one small cluster at the bottom.
    //
    // Megan, 16 Aug, on a screenshot of a bead chain showing eleven:
    // "That is a chain where the charms dangle at the bottom, so there is only
    // 3 charms suppose to be there" — "11 spaces instead of 3 at the bottom.
    // For all of them."
    //
    // This check MUST come before the necklace early-return below. When that
    // return was added it sat above this branch, which made the whole dangle
    // rule unreachable in necklace mode and is why every dangle chain was
    // offering eleven.
    const dangleOnly = !!this.selectedBase?.dangleOnly;

    // NECKLACE, open-linked: the stored positions are final. Every one was
    // re-traced along the bottom band with exactly 11 spots. Re-applying a
    // bottom-band filter here measured that band against ITSELF and kept the
    // middle 55%, which is how a chain published with 11 offered only 7
    // (Megan: "all the necklaces need 11 holes").
    //
    // The crop band is derived from whatever this returns, so widening the
    // list widens the viewport with it — no spot lands off-frame.
    if (!dangleOnly) return holes;

    // Solid weave clusters tighter than a necklace drape does.
    const pct = Math.min(this.necklaceBottomPct, this.dangleBottomPct);

    const ys = holes.map(h => h[1]);
    const lo = Math.min(...ys), hi = Math.max(...ys);
    const span = hi - lo;
    if (span <= 0) return holes;
    const cut = hi - span * (pct / 100);
    let kept = holes.filter(h => h[1] >= cut);
    // Never strip so far that there's nowhere to hang anything.
    if (kept.length < 3) kept = holes;

    // A solid-weave chain also can't carry as many charms — they'd collide once
    // they've all slid together — so cap the offered count.
    if (kept.length > this.dangleMaxSlots) {
      // Keep the most central run so the cluster reads as centred on the wrist.
      const sorted = [...kept].sort((a, b) => a[0] - b[0]);
      const start = Math.floor((sorted.length - this.dangleMaxSlots) / 2);
      kept = sorted.slice(start, start + this.dangleMaxSlots);
    }
    return kept;
  }

  /**
   * Megan's fill order: charm 1 sits at the BOTTOM CENTRE, then they work
   * outwards alternating sides — evens up the right, odds up the left, for
   * bracelets and necklaces alike.
   *
   * Her later note pins the necklace row down exactly, read left to right:
   *     11  9  7  5  3  1  2  4  6  8  10
   * which puts the odd numbers on the left and the evens on the right. That
   * is the mirror of the necklace half of her earlier notebook drawing, so
   * both modes now share one rule.
   * The traced hole list arrives in whatever order the chain was walked,
   * which is arbitrary, so re-order it here. Everything downstream — the
   * numbers drawn in each empty spot, which spot a tap fills next, and the
   * _slot_index sent to the cart — follows this array.
   */
  orderForFill(holes) {
    if (!holes || holes.length < 3) return holes;
    const pts = holes.map((h, i) => ({ h, x: h[0], y: h[1] }));
    // 1 = the lowest point on the chain
    const start = pts.reduce((lo, p) => (p.y > lo.y ? p : lo), pts[0]);
    const rest = pts.filter(p => p !== start);
    const dist = p => Math.hypot(p.x - start.x, (p.y - start.y) * 4 / 3);
    const left  = rest.filter(p => p.x <  start.x).sort((a, b) => dist(a) - dist(b));
    const right = rest.filter(p => p.x >= start.x).sort((a, b) => dist(a) - dist(b));
    // evens right, odds left — both modes (see her 11 9 7 5 3 1 2 4 6 8 10)
    const [evens, odds] = [right, left];
    const out = [start.h];
    for (let i = 0; i < Math.max(evens.length, odds.length); i++) {
      if (evens[i]) out.push(evens[i].h);   // 2, 4, 6 ...
      if (odds[i])  out.push(odds[i].h);    // 3, 5, 7 ...
    }
    return out;
  }

  applySlotLayout() {
    const holes = this.selectedBase?.chainHoles;
    const bracelet = this.querySelector('[data-bracelet-img]');
    const slotsList = this.querySelector('[data-slots]');

    // Slot placement uses the (possibly narrowed) list; the loop/zoom maths
    // below deliberately keeps using the full `holes` trace.
    const slotHoles = this.orderForFill(this.slotHolesFor(holes));
    this.activeHoles = slotHoles;

    if (holes && holes.length) {
      // Grow the slot pool if the bracelet has MORE holes than the
      // Liquid template pre-rendered. Extras are clones of slot 0.
      while (this.slots.length < slotHoles.length && slotsList && this.slots[0]) {
        const clone = this.slots[0].cloneNode(false);
        clone.dataset.slot = String(this.slots.length);
        clone.setAttribute('aria-label', `Slot ${this.slots.length + 1} — empty`);
        clone.addEventListener('dragover', (e) => { e.preventDefault(); clone.classList.add('is-drag-over'); });
        clone.addEventListener('dragleave', () => clone.classList.remove('is-drag-over'));
        clone.addEventListener('drop', (e) => { e.preventDefault(); clone.classList.remove('is-drag-over'); this.onDrop(e, clone); });
        clone.addEventListener('keydown', (e) => {
          if ((e.key === 'Delete' || e.key === 'Backspace') && this.selectedCharms[parseInt(clone.dataset.slot, 10)]) {
            this.removeSlot(parseInt(clone.dataset.slot, 10));
          }
        });
        slotsList.appendChild(clone);
        this.slots.push(clone);
      }

      // Midline of the traced loop — the line above which a bracelet's charms
      // splay upward. Taken from the holes themselves rather than the canvas
      // centre, since a bracelet does not sit in the middle of its frame.
      const holeYs = slotHoles.map(h => h[1]);
      const midY = holeYs.length ? (Math.min(...holeYs) + Math.max(...holeYs)) / 2 : 50;

      // Hide any slots past the traced hole count — the physical chain
      // literally doesn't have more attach points on this bracelet.
      this.slots.forEach((slot, i) => {
        const hole = slotHoles[i];
        if (!hole) {
          slot.style.display = 'none';
          return;
        }
        slot.style.display = '';
        // Anchor the slot's RING POINT (11% down from the slot top — matches
        // --ring-y in the section CSS) on the traced hole. The jump ring
        // sits IN the hole and the charm hangs BELOW it, like a real charm
        // on a jump ring — instead of the charm being centred over the chain.
        slot.style.top = hole[1] + '%';
        slot.style.left = hole[0] + '%';
        // A bracelet lies flat and is shot from above, so its charms splay
        // OUTWARD from the loop: the ones on the top half point up, not down
        // through the chain. Anchoring stays on the traced hole either way —
        // only which side of it the charm hangs from changes. A necklace is
        // worn hanging, so every charm on one points down.
        const up = this.mode === 'bracelet' && hole[1] < midY;
        slot.classList.toggle('is-up', up);
        slot.style.transform = up ? 'translate(-50%, -89%)' : 'translate(-50%, -11%)';
        slot.style.setProperty('--slot-idx', String(i));
        slot.style.setProperty('--slot-count', String(slotHoles.length));
      });
      // Also expose the effective slot count for the summary UI.
      this.slotCount = slotHoles.length;
      // Update the heading HERE, where the real count is finally known. The
      // shared sync runs earlier in the cycle, so it was still echoing
      // Liquid's max_slots — Megan's screenshot showed "Add up to 18 charms"
      // above a counter reading "0 of 11" on the same chain.
      this.querySelectorAll('[data-max-slots], [data-max-slots-inline]')
        .forEach(el => { el.textContent = String(this.slotCount); });
      // Positioning is now independent of the sway container's rotation
      // reference — reset transform-origin so removing display:none doesn't
      // reintroduce inherited weirdness on next base swap.
      if (bracelet) bracelet.dataset.layout = 'holes';

      // ---- Physical scale + framing, derived from the traced holes ----
      // Canvas is 3:4, so 1% of height = 4/3 of a width-unit (cqi).
      const pts = holes.map(h => [h[0], h[1] * (4 / 3)]);
      let loop = 0;
      let minDist = Infinity;
      for (let i = 0; i < pts.length; i++) {
        const a = pts[i], b = pts[(i + 1) % pts.length];
        const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
        loop += d;
        if (i < pts.length - 1) minDist = Math.min(minDist, d);
      }
      // mm↔cqi bridge: the visible loop IS the physical chain length.
      this.loopLenCqi = loop;
      // HITBOX: each slot's clickable box spans ~90% of the gap to its
      // nearest neighbour, so hitboxes never overlap and a click lands on
      // the hole you aimed at.
      const slotW = Math.max(5.5, Math.min(14, minDist * 0.9));
      this.style.setProperty('--slot-w', slotW.toFixed(1) + 'cqi');
      // ZOOM: scale chain + slots together so the jewelry fills the canvas
      // instead of floating in studio whitespace. Padding leaves room for
      // charms hanging below the lowest hole.
      const xs = holes.map(h => h[0]);
      const ys = holes.map(h => h[1]);
      const spanW = Math.min(100, (Math.max(...xs) - Math.min(...xs)) + 26);
      const spanH = Math.min(100, (Math.max(...ys) - Math.min(...ys)) + 30);
      const zoom = Math.max(1, Math.min(1.45, Math.min(100 / spanW, 100 / spanH)));
      const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
      const cy = (Math.min(...ys) - 9 + Math.max(...ys) + 21) / 2;
      this._fitZoom = zoom;
      this._zoomOriginY = cy;
      this.style.setProperty('--cb-zoom-origin', cx.toFixed(1) + '% ' + cy.toFixed(1) + '%');

      // The half-necklace band, measured off the holes that actually take a
      // charm rather than the whole trace: a little chain visible above the
      // topmost one, and room below the lowest for the charms to hang in.
      const bandPts = (slotHoles && slotHoles.length ? slotHoles : holes).map(h => h[1]);
      this._cropBand = bandPts.length
        ? { top: Math.min(...bandPts) - 9, bottom: Math.max(...bandPts) + 30 }
        : null;

      this.updateZoom();
    } else {
      // NO ellipse fallback — the floating oval looked detached from the
      // chain. Until hole positions exist (metafield, or the in-browser
      // photo detection that autoDetectHoles() kicks off right after this)
      // slots stay hidden entirely. On the rare hard failure the shopper
      // gets a status message instead of slots hovering in whitespace.
      this.slotCount = parseInt(this.dataset.slotCount || '5', 10);
      this.slots.forEach((slot) => { slot.style.display = 'none'; });
      if (bracelet) bracelet.dataset.layout = 'detecting';
      this.loopLenCqi = null;
      this._cropBand = null;
      this._zoomOriginY = null;
      this.style.removeProperty('--cb-zoom');
      this.style.removeProperty('--cb-zoom-origin');
      this.style.removeProperty('--slot-w');
      this.applyCrop(1);
    }
    // Truncate selectedCharms to the new count so overflow charms drop off.
    if (this.selectedCharms.length !== this.slotCount) {
      const existing = this.selectedCharms.slice(0, this.slotCount);
      while (existing.length < this.slotCount) existing.push(null);
      this.selectedCharms = existing;
    }
    // Every "max charms" figure must track the SELECTED chain's real attach
    // points. Liquid seeds these with a theoretical max (bracelet length /
    // charm width = 18) which ignores the actual chain, so a chain with 11
    // traced holes was advertising 18. The traced count is the truth.
    //
    // Previously this also rewrote the heading of '#cb-step2-heading' — that id
    // became "2. Pick your size" when the steps were renumbered, so it was
    // clobbering the size heading while leaving the real count stale. The count
    // lives in a [data-max-slots-inline] span inside the heading, so updating
    // the spans is sufficient and can't hit the wrong element.
    this.querySelectorAll('[data-max-slots], [data-max-slots-inline]')
      .forEach(el => { el.textContent = String(this.slotCount); });

    // "0 of 18" in the same heading is seeded from Liquid too.
    if (this.slotSummary) {
      const filled = this.selectedCharms.filter(Boolean).length;
      this.slotSummary.textContent = `${filled} of ${this.slotCount}`;
    }

    // Solid weave: explain that charms gather at the bottom.
    const note = this.querySelector('[data-dangle-note]');
    if (note) note.hidden = !this.selectedBase?.dangleOnly;
  }

  /**
   * Process a charm photo ONCE into a clean cutout (cached per URL):
   *   1. chroma-key the white studio backdrop (same knee as #cb-white-out)
   *   2. CROP to the visible charm — the photo's white padding otherwise
   *      still occupies layout space, leaving a phantom gap between the
   *      jump ring and the charm ("floating charm" bug)
   *   3. locate the BAIL (content centroid of the top rows) so the charm
   *      can be shifted to hang from its actual attachment loop.
   * Returns a cache entry {url, aspect, bailShift, canvas, promise}.
   */
  processCharm(src) {
    if (!this._procCache) this._procCache = new Map();
    let entry = this._procCache.get(src);
    if (entry) return entry;
    entry = { url: null, aspect: null, bailShift: 0, canvas: null, promise: null };
    entry.promise = new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const s = Math.min(1, 260 / Math.max(img.naturalWidth || 1, img.naturalHeight || 1));
          const w = Math.max(1, Math.round(img.naturalWidth * s));
          const h = Math.max(1, Math.round(img.naturalHeight * s));
          const c = document.createElement('canvas');
          c.width = w; c.height = h;
          const ctx = c.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          const d = ctx.getImageData(0, 0, w, h);
          const p = d.data;
          let minX = w, maxX = -1, minY = h, maxY = -1;
          for (let y = 0; y < h; y++) {
            for (let x = 0; x < w; x++) {
              const i = (y * w + x) * 4;
              const a = Math.max(0, Math.min(255, 23 * 255 - 8 * (p[i] + p[i + 1] + p[i + 2])));
              if (a < p[i + 3]) p[i + 3] = a;
              if (p[i + 3] > 40) {
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          if (maxX <= minX || maxY <= minY) { resolve(entry); return; }
          ctx.putImageData(d, 0, 0);
          const cw = maxX - minX + 1, chh = maxY - minY + 1;
          const c2 = document.createElement('canvas');
          c2.width = cw; c2.height = chh;
          const ctx2 = c2.getContext('2d');
          ctx2.drawImage(c, minX, minY, cw, chh, 0, 0, cw, chh);
          // Bail = centroid of content in the crop's top 12% of rows.
          const topRows = Math.max(2, Math.round(chh * 0.12));
          const td = ctx2.getImageData(0, 0, cw, topRows).data;
          let sx = 0, n = 0;
          for (let y = 0; y < topRows; y++) {
            for (let x = 0; x < cw; x++) {
              if (td[(y * cw + x) * 4 + 3] > 40) { sx += x; n++; }
            }
          }
          const bailX = n ? sx / n : cw / 2;
          entry.url = c2.toDataURL('image/png');
          entry.aspect = cw / chh;
          entry.bailShift = ((cw / 2 - bailX) / cw) * 100;  // % of width, + = shift right
          entry.canvas = c2;
        } catch { /* CORS-tainted — leave entry empty, callers fall back */ }
        resolve(entry);
      };
      img.onerror = () => resolve(entry);
      img.src = src;
    });
    this._procCache.set(src, entry);
    return entry;
  }

  /**
   * (Re)bind palette drag/tap/hover handlers. Safe to call after the
   * palette DOM is swapped out — dedupes via a `_cbBound` flag on each
   * button so re-clicking a pill doesn't stack duplicate listeners.
   */
  rewireCharms() {
    this.charmButtons = Array.from(this.querySelectorAll('[data-charm]'));
    this.charmButtons.forEach(btn => {
      if (btn._cbBound) return;
      btn._cbBound = true;
      btn.addEventListener('dragstart', (e) => this.onCharmDragStart(e, btn));
      btn.addEventListener('click', (e) => { e.preventDefault(); this.onCharmClick(btn); });
      // Pre-build a transparent drag ghost on hover: the browser's native
      // drag preview rasterizes the RAW <img> — CSS filters (the white-out)
      // don't apply — so without this the shopper drags a white box around.
      btn.addEventListener('pointerenter', () => this.prepareGhost(btn), { passive: true });
      btn.addEventListener('touchstart', () => this.prepareGhost(btn), { passive: true });
    });
  }

  /** Transparent drag ghost from the processed cutout (kicked off on hover). */
  prepareGhost(btn) {
    if (btn._ghostPrep) return;
    btn._ghostPrep = true;
    const src = btn.dataset.image;
    if (!src) return;
    const entry = this.processCharm(src);
    const set = () => {
      if (!entry.canvas) return;
      entry.canvas.style.cssText = 'position:fixed;top:-300px;left:-300px;pointer-events:none;';
      btn._ghost = entry.canvas;
    };
    entry.canvas ? set() : entry.promise.then(set);
  }

  onCharmDragStart(e, btn) {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify(this.charmDataFrom(btn)));
    this.prepareGhost(btn);
    if (btn._ghost) {
      // Element must be in the DOM for setDragImage — park it off-screen
      // for a beat, then remove.
      document.body.appendChild(btn._ghost);
      e.dataTransfer.setDragImage(btn._ghost, btn._ghost.width / 2, btn._ghost.height / 2);
      const g = btn._ghost;
      setTimeout(() => g.remove(), 0);
    }
  }

  /**
   * Click on a palette charm:
   *   - If nothing is staged → stage this charm (waiting for slot pick).
   *   - If THIS charm is already staged → auto-fill first empty slot.
   *   - If a DIFFERENT charm is staged → replace with this one.
   */
  onCharmClick(btn) {
    // TOUCH DEVICES: one tap = placed. Megan: "dragging doesn't work on
    // phone... I don't think people are smart enough for clicking" — so on
    // a phone a single tap drops the charm straight into the next free
    // numbered spot instead of the two-step stage-then-tap-a-slot dance.
    if (this.isTouch()) {
      this.addToFirstEmpty(btn);
      return;
    }
    if (this.stagedCharm === btn) {
      this.clearStagedCharm();
      this.addToFirstEmpty(btn);
      return;
    }
    if (this.stagedCharm) this.stagedCharm.classList.remove('is-staged');
    this.stagedCharm = btn;
    btn.classList.add('is-staged');
    this.setStatus(`Tap a slot on the bracelet to place "${btn.dataset.title}" — or tap the charm again to auto-place.`, '');
  }

  /** Coarse pointer = finger. Desktop keeps drag + stage-and-place. */
  isTouch() {
    return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  }

  /**
   * Click on a slot: if a charm is staged, drop it in this slot (or
   * replace any existing charm there). No staged charm = no-op — clicks
   * on empty slots don't do anything without a chosen charm.
   */
  onSlotClick(e, slot) {
    // Tapping a spot you passed over takes it back, with or without a charm
    // staged — otherwise a skip is one-way and you have to Clear all.
    const sIdx = parseInt(slot.dataset.slot, 10);
    if (this.skippedSlots.has(sIdx) && !this.selectedCharms[sIdx]) {
      this.skippedSlots.delete(sIdx);
      this.render();
      this.persist();
      if (!this.stagedCharm) return;
    }
    if (!this.stagedCharm) return;
    // Don't intercept clicks on the remove button
    if (e.target && e.target.classList && e.target.classList.contains('charm-bar__slot-remove')) return;
    // Same nearest-hole routing as drops — overlapping boxes otherwise
    // send the tap to the hole above the one the shopper aimed at.
    const target = this.nearestSlot(e.clientX, e.clientY) || slot;
    const idx = parseInt(target.dataset.slot, 10);
    this.setSlot(idx, this.charmDataFrom(this.stagedCharm));
    this.clearStagedCharm();
  }

  clearStagedCharm() {
    if (this.stagedCharm) this.stagedCharm.classList.remove('is-staged');
    this.stagedCharm = null;
    this.setStatus('', '');
  }

  charmDataFrom(btn) {
    return {
      variantId: btn.dataset.variantId,
      productId: btn.dataset.productId,
      title: btn.dataset.title,
      price: parseInt(btn.dataset.price, 10),
      image: btn.dataset.image,
      widthMm: parseFloat(btn.dataset.widthMm) || null,
      heightMm: parseFloat(btn.dataset.heightMm) || null,
    };
  }

  /**
   * The slot whose HOLE (ring point, 11% from the box top) is nearest the
   * pointer. Hit-testing by element is wrong here: every slot's box hangs
   * BELOW its hole, so the hole a shopper points at is usually covered by
   * the box of the hole ABOVE it — drops kept landing one hole north.
   */
  nearestSlot(x, y) {
    let best = null, bd = Infinity;
    this.slots.forEach((slot) => {
      if (slot.hidden || slot.style.display === 'none') return;
      const r = slot.getBoundingClientRect();
      if (!r.width) return;
      const dx = (r.left + r.width / 2) - x;
      const dy = (r.top + r.height * 0.11) - y;
      const d = dx * dx + dy * dy;
      if (d < bd) { bd = d; best = slot; }
    });
    return best;
  }

  onDrop(e, slot) {
    if (e._cbHandled) return;   // slot handler + canvas handler both fire
    e._cbHandled = true;
    const target = this.nearestSlot(e.clientX, e.clientY) || slot;
    if (!target) return;
    const idx = parseInt(target.dataset.slot, 10);
    let payload;
    try { payload = JSON.parse(e.dataTransfer.getData('application/json')); } catch { return; }
    if (!payload || !payload.variantId) return;
    this.setSlot(idx, payload);
  }

  setSlot(idx, charm) {
    this.selectedCharms[idx] = charm;
    this.render();
    this.persist();
    // Trigger drop-in animation on that specific slot
    const slotEl = this.slots[idx];
    if (slotEl) {
      slotEl.classList.remove('is-just-added');
      void slotEl.offsetWidth;
      slotEl.classList.add('is-just-added');
      setTimeout(() => slotEl.classList.remove('is-just-added'), 800);
    }
    // Update "chain is full" state
    if (this.canvas) {
      const isFull = this.selectedCharms.every(Boolean);
      this.canvas.classList.toggle('is-full', isFull);
    }
  }

  removeSlot(idx) {
    this.selectedCharms[idx] = null;
    this.render();
    this.persist();
    if (this.canvas) this.canvas.classList.remove('is-full');
  }

  /**
   * Start over. Megan: "there also needs to be a clear all button, so people
   * can restart it if they want."
   *
   * Confirmed first — undoing a dozen individually-placed charms by hand is
   * exactly the mistake worth guarding against, and there is no undo.
   */
  clearAllCharms() {
    const n = this.selectedCharms.filter(Boolean).length;
    if (!n) return;
    if (!window.confirm(`Remove all ${n} charm${n === 1 ? '' : 's'} and start over?`)) return;
    this.selectedCharms = this.selectedCharms.map(() => null);
    this.skippedSlots.clear();
    this.clearStagedCharm();
    this.render();
    this.persist();
    if (this.canvas) this.canvas.classList.remove('is-full');
    this.setStatus('Cleared — pick your charms again.', '');
  }

  /** Index of the next spot auto-place would use — empty and not passed over. */
  nextFreeIndex() {
    return this.selectedCharms.findIndex((c, i) => c === null && !this.skippedSlots.has(i));
  }

  /** Leave the next spot empty and move on to the one after it. */
  skipNextSpot() {
    const idx = this.nextFreeIndex();
    if (idx === -1) return;
    this.skippedSlots.add(idx);
    this.render();
    this.persist();
  }

  addToFirstEmpty(btn) {
    const idx = this.nextFreeIndex();
    if (idx === -1) {
      this.setStatus(`Bracelet is full — ${this.slotCount} charms max. Remove one to swap.`, 'error');
      // Visual: shake all filled slots so they see the collision limit
      this.slots.forEach((s) => {
        s.classList.remove('is-rejected');
        void s.offsetWidth;
        s.classList.add('is-rejected');
        setTimeout(() => s.classList.remove('is-rejected'), 400);
      });
      return;
    }
    this.setSlot(idx, this.charmDataFrom(btn));
    this.setStatus('', '');
  }

  setStatus(message, tone) {
    this.statusEl.textContent = message;
    this.statusEl.classList.remove('is-error', 'is-success');
    if (tone === 'error') this.statusEl.classList.add('is-error');
    if (tone === 'success') this.statusEl.classList.add('is-success');
  }

  persist() {
    writeState({
      baseVariantId: this.selectedBase?.variantId || null,
      charms: this.selectedCharms,
    });
  }

  /**
   * Real-charm crowding: when two placed charms would overlap, tilt each
   * away from the other around its BAIL (the jump-ring pivot) — exactly
   * how neighbouring charms push apart on a physical bracelet. Tilt grows
   * with the amount of overlap; isolated charms hang straight down.
   */
  layoutDangle() {
    // Must be the SAME list applySlotLayout placed the slots from, otherwise
    // slot i would be measured against the wrong hole once necklace mode
    // narrows the set.
    const holes = this.activeHoles || this.selectedBase?.chainHoles;
    if (!holes) return;
    const items = [];
    this.slots.forEach((slot, i) => {
      const charm = this.selectedCharms[i];
      const hole = holes[i];
      if (!charm || !hole || slot.style.display === 'none') return;
      const w = parseFloat(slot.style.width) || 10;              // cqi
      const aspect = parseFloat(slot.style.aspectRatio) || 0.8;
      const h = w / aspect;                                       // cqi
      items.push({ i, slot, x: hole[0], y: hole[1] * 4 / 3, w, h });
    });
    const tilt = {};
    items.forEach(it => { tilt[it.i] = 0; });
    for (let a = 0; a < items.length; a++) {
      for (let b = a + 1; b < items.length; b++) {
        const A = items[a], B = items[b];
        // FULL rectangle overlap — a tall charm hangs h cqi BELOW its hole,
        // so charms on vertically adjacent holes overlap top-over-bottom
        // even when their widths never touch (the two-fish case).
        const ox = Math.min(A.x + A.w / 2, B.x + B.w / 2) - Math.max(A.x - A.w / 2, B.x - B.w / 2);
        const oy = Math.min(A.y + A.h, B.y + B.h) - Math.max(A.y, B.y);
        if (ox <= 0 || oy <= 0) continue;
        // Severity: how much of the NARROWER charm's width is covered,
        // gated by whether the bodies genuinely share vertical space.
        // (The old product-of-fractions scored a long charm draped fully
        // over its neighbour at ~0.1 → an invisible 3° tilt.)
        const amt = Math.min(1, ox / (0.6 * Math.min(A.w, B.w)))
                  * Math.min(1, oy / (0.5 * Math.min(A.h, B.h)));
        // Splay horizontally away from each other; when perfectly stacked,
        // the upper one goes one way and the lower the other.
        let dir = Math.sign(B.x - A.x);
        if (!dir) dir = (A.y <= B.y) ? 1 : -1;
        tilt[A.i] -= 30 * amt * dir;
        tilt[B.i] += 30 * amt * dir;
      }
    }
    items.forEach(it => {
      const img = it.slot.querySelector('img');
      if (!img) return;
      const deg = Math.max(-34, Math.min(34, tilt[it.i]));
      img.style.rotate = deg ? deg.toFixed(1) + 'deg' : '0deg';
      const shift = parseFloat(img.dataset.bailShift || '0');
      img.style.transformOrigin = (50 - shift).toFixed(1) + '% 3%';
      img.style.transition = 'rotate 0.25s ease';
    });
  }

  /**
   * Pixel-accurate hover for stacked charms: the charm whose actual CUTOUT
   * (not its invisible bounding box) is under the pointer pops to front —
   * clicking "the white stuff" of a front charm no longer hides the charm
   * you're really pointing at.
   */
  updateAlphaHover(e) {
    let winner = null;
    const els = document.elementsFromPoint(e.clientX, e.clientY);
    for (const el of els) {
      if (!el.classList || !el.classList.contains('charm-bar__slot')) continue;
      if (!el.classList.contains('is-filled')) continue;
      const i = parseInt(el.dataset.slot, 10);
      const charm = this.selectedCharms[i];
      const proc = charm && charm.image && this._procCache ? this._procCache.get(charm.image) : null;
      const img = el.querySelector('img');
      if (!proc || !proc.canvas || !img) { winner = winner || el; continue; }
      const r = img.getBoundingClientRect();
      if (!r.width || !r.height) continue;
      const px = Math.floor(((e.clientX - r.left) / r.width) * proc.canvas.width);
      const py = Math.floor(((e.clientY - r.top) / r.height) * proc.canvas.height);
      if (px < 0 || py < 0 || px >= proc.canvas.width || py >= proc.canvas.height) continue;
      try {
        if (proc.canvas.getContext('2d').getImageData(px, py, 1, 1).data[3] > 30) {
          winner = el;
          break;
        }
      } catch { winner = winner || el; }
    }
    this.slots.forEach(s => s.classList.toggle('is-front', s === winner));
  }

  render() {
    // Slots — apply per-charm real-world size if the placed charm has
    // width/height metafields; otherwise fall back to the section
    // default via CSS var --charm-pct.
    this.slots.forEach((slot, i) => {
      const charm = this.selectedCharms[i];
      slot.classList.toggle('is-filled', !!charm);
      slot.innerHTML = '';
      if (charm) {
        slot.setAttribute('aria-label', `Slot ${i + 1} — ${charm.title}`);
        // Per-charm size override: width as % of bracelet length, clamped
        // 12-18 cqi. Bumped ~1.5x from the original 8-14 range per Meg's
        // "0.5x bigger, just a bit" feedback. Positioning (ring-in-hole +
        // charm-below) stays the same — only the visible width changes.
        if (charm.widthMm && this.braceletLenMm && this.loopLenCqi) {
          // TRUE physical scale: the traced hole loop's on-screen length IS
          // the chain's real length, which gives an exact mm↔screen bridge.
          // One uniform ×1.35 readability boost keeps RELATIVE sizes exact —
          // an 18mm charm genuinely reads twice a 9mm one, so shoppers see
          // real size accuracy. Clamps only rescue extremes (tap targets /
          // photo-dominating giants).
          // NO boost factor — Megan's width_mm IS the size. The chain loop
          // on screen equals the chain's real length, so widthMm × cqiPerMm
          // is the charm's exact on-screen size. Tiny floor only so charms
          // stay grabbable; generous ceiling for oversize statement pieces.
          // Prefer the MEASURED chain path over the hole polygon.
          //
          // loopLenCqi is the perimeter of the polygon through the traced
          // holes. On a bracelet that is nearly the chain — the holes wrap the
          // whole closed loop — and it only under-reads by ~1.2x from cutting
          // corners between points. On a NECKLACE it is wrong by about 2x,
          // because the holes trace the bottom charm-bearing curve alone while
          // braceletLenMm is the full necklace. Measured on the Gold Rolo
          // Cable at 16": polygon 110.3 vs real path 250.5, so cqiPerMm came
          // out 0.271, a 12mm charm computed to 3.26 cqi and hit the 3.5 floor
          // clamp — every charm on every necklace rendered at the minimum, and
          // a 25mm charm looked identical to a 9mm one.
          //
          // chain.loop_cqi is the real drawn path, measured offline from the
          // chain photo by scripts/shopify-admin/chain-loop-length.py.
          const loopCqi = this.selectedBase?.loopCqi || this.loopLenCqi;
          const cqiPerMm = (loopCqi / this.braceletLenMm) * this.charmBoost;
          const hMmForW = charm.heightMm || charm.widthMm;
          const trueW = charm.widthMm * cqiPerMm;
          const trueH = hMmForW * cqiPerMm;

          // FLOOR ON THE LARGER DIMENSION, not on width.
          //
          // The floor is only there so a tiny charm stays grabbable. Applying
          // it to width alone silently fattened narrow-but-tall charms: the
          // box is width-driven, so a 5x20mm bullet clamped from 3.08 to 3.50
          // cqi came out 15.73 cqi tall against 13.85 for a 13x20mm fairy dust
          // pouch — 14% taller than a charm that is exactly the same height in
          // real life. Megan spotted it on the two side by side.
          //
          // Scaling by whichever dimension is smallest-relative keeps the
          // charm's true proportions and only rescues charms that really are
          // tiny in BOTH directions.
          const largest = Math.max(trueW, trueH);
          const rescue = largest < 3.5 ? 3.5 / largest : 1;
          setSlotWidth(slot, Math.min(26, trueW * rescue));
          // Box proportions come from the MEASURED mm, never from the photo.
          // The cutout often includes a long bail, so using the image's own
          // aspect stretched tall charms (an 11x19mm boot rendered ~2.8x too
          // tall and read as bigger than a 25x30mm flower).
          // The charm body hangs below the ring, which occupies --ring-y of
          // the box, so the box is slightly taller than the charm itself.
          const hMm = charm.heightMm || charm.widthMm;
          slot.style.aspectRatio = String(Math.max(0.18, Math.min(2.6, 0.89 * charm.widthMm / hMm)));
        } else if (charm.widthMm && this.braceletLenMm) {
          // No traced loop (ellipse fallback) — keep the uniform look.
          setSlotWidth(slot, 24);
          const hMm = charm.heightMm || charm.widthMm;
          const rawAspect = charm.widthMm / (hMm * 1.35);
          const clampedAspect = Math.max(0.55, Math.min(1.0, rawAspect));
          slot.style.aspectRatio = String(clampedAspect);
        } else {
          slot.style.removeProperty('width');
          slot.style.removeProperty('aspect-ratio');
        }
        if (charm.image) {
          const img = document.createElement('img');
          img.alt = charm.title;
          // Use the processed cutout: white keyed AND cropped to the actual
          // charm, so its top edge really touches the jump ring (the raw
          // photo's white padding left a phantom gap), and shifted so the
          // BAIL — not the photo centre — hangs from the ring.
          const proc = this.processCharm(charm.image);
          const applyProc = () => {
            if (!slot.isConnected || this.selectedCharms[i] !== charm) return;
            if (!proc.url) return;
            img.src = proc.url;
            img.style.transform = 'translateX(' + proc.bailShift.toFixed(1) + '%)';
            img.dataset.bailShift = proc.bailShift.toFixed(1);
            // Slot aspect from the VISIBLE charm's proportions (+ ring headroom)
            // Only let the photo dictate shape when we have NO measurement.
            if (!(charm.widthMm && charm.heightMm)) {
              slot.style.aspectRatio = String(Math.max(0.35, Math.min(1.4, proc.aspect / 1.25)));
            }
            // Aspect (and so true height) just changed — recheck overlaps.
            this.layoutDangle();
          };
          if (proc.url) {
            applyProc();
          } else {
            img.src = charm.image;
            proc.promise.then(applyProc);
          }
          slot.appendChild(img);
        }
        const rm = document.createElement('button');
        rm.type = 'button';
        rm.className = 'charm-bar__slot-remove';
        rm.setAttribute('aria-label', `Remove ${charm.title}`);
        rm.textContent = '×';
        rm.addEventListener('click', (e) => { e.stopPropagation(); this.removeSlot(i); });
        slot.appendChild(rm);
      } else {
        slot.setAttribute('aria-label', `Slot ${i + 1} — empty`);
        // Number every empty spot (Megan: "select Charms 1~11") so the
        // order charms will fill is obvious, especially on a phone.
        const num = document.createElement('span');
        num.className = 'charm-bar__slot-num';
        num.textContent = String(i + 1);
        slot.appendChild(num);
        // Reset to CSS default sizing for empty slot markers
        slot.style.removeProperty('width');
        slot.style.removeProperty('aspect-ratio');
      }
    });

    // Crowding: fan overlapping charms apart like real charms on a chain.
    this.layoutDangle();

    // Totals
    const filled = this.selectedCharms.filter(Boolean);

    // Nothing to preview until at least one charm is on the chain; and if the
    // last one is taken off while previewing, drop back to editing so the
    // shopper is not left looking at a canvas with no way to place anything.
    // A skipped spot that has since been filled is no longer skipped.
    this.skippedSlots.forEach(i => { if (this.selectedCharms[i]) this.skippedSlots.delete(i); });
    this.slots.forEach((s, i) => s.classList.toggle('is-skipped', this.skippedSlots.has(i)));
    const skp = this.querySelector('[data-skip-charm]');
    if (skp) {
      skp.hidden = !this.selectedBase;
      skp.disabled = this.nextFreeIndex() === -1;
    }

    const clr = this.querySelector('[data-clear-all]');
    if (clr) clr.hidden = filled.length === 0;
    const pv = this.querySelector('[data-preview-toggle]');
    if (pv) {
      pv.hidden = filled.length === 0;
      if (!filled.length && this.canvas?.classList.contains('is-preview')) {
        this.canvas.classList.remove('is-preview');
        pv.setAttribute('aria-pressed', 'false');
        const label = pv.querySelector('[data-preview-label]');
        if (label) label.textContent = 'Preview';
      }
    }

    const charmSubtotal = filled.reduce((sum, c) => sum + c.price, 0);
    const basePrice = this.selectedBase?.price || 0;
    const extraPrice = (this.extraSteps || 0)
      * (parseInt(this.extraWrap?.dataset.addonPrice, 10) || 0);

    this.slotSummary.textContent = `${filled.length} of ${this.slotCount}`;
    this.countEl.textContent = filled.length;
    this.totalBaseEl.textContent = this.selectedBase ? formatMoney(basePrice, this.moneyFormat) + ` — ${this.selectedBase.title}` : '—';
    this.totalCharmsEl.textContent = filled.length ? formatMoney(charmSubtotal, this.moneyFormat) : '—';
    this.totalGrandEl.textContent = (this.selectedBase || filled.length)
      ? formatMoney(basePrice + charmSubtotal + extraPrice, this.moneyFormat) : '—';

    // ODD CHARM COUNT ON NECKLACES.
    //
    // Megan: a necklace only hangs centred with an odd number of charms —
    // one sits at the bottom centre and the rest pair off either side of it.
    // An even count leaves the middle straddling the clasp line and the whole
    // piece reads crooked. Bracelets are a closed loop worn all the way round,
    // so they don't care.
    const needsOdd = this.mode === 'necklace' && filled.length > 0 && filled.length % 2 === 0;
    this.addBtn.disabled = !this.selectedBase || filled.length === 0 || needsOdd;
    const oddNote = this.querySelector('[data-odd-note]');
    if (oddNote) {
      oddNote.hidden = !needsOdd;
      if (needsOdd) {
        oddNote.textContent =
          `${filled.length} charms won't hang centred on a necklace — one charm sits at the `
          + `bottom centre and the rest pair off either side of it. Add one or take one away `
          + `so you have an odd number (${filled.length - 1} or ${filled.length + 1}).`;
      }
    }
  }

  async addToCart() {
    if (this.addBtn.disabled) return;
    const filled = this.selectedCharms.filter(Boolean);
    if (!this.selectedBase || filled.length === 0) return;

    const buildId = uuid();

    // Snapshot the current canvas (chain + placed charms) so fulfillment
    // can see the exact layout in the admin order view. Renders to a
    // low-res JPEG and embeds as a data URL in the base line item.
    // Fails-open: if capture throws, cart still submits without the image.
    this.addBtn.disabled = true;
    this.setStatus('Capturing your build…', '');
    let buildImage = '';
    let buildSummary = this.buildTextSummary();
    try {
      buildImage = await this.captureBuildImage();
    } catch (e) {
      console.warn('[charm-bar] build-image capture failed:', e);
    }

    const items = [
      {
        id: parseInt(this.selectedBase.variantId, 10),
        quantity: 1,
        properties: {
          _bracelet_build_id: buildId,
          _role: 'base',
          _charms_selected: String(filled.length),
          _bracelet_build_summary: buildSummary,
          ...(buildImage ? { _bracelet_build_image: buildImage } : {}),
        },
      },
      // NOTE: index into selectedCharms, NOT the filtered list. Using the
      // filtered index reported charms in spots 1,5,9 as "1,2,3", which is
      // the wrong position to assemble from.
      ...this.selectedCharms
        .map((c, slotIdx) => ({ c, slotIdx }))
        .filter(x => x.c)
        .map(({ c, slotIdx }) => ({
          id: parseInt(c.variantId, 10),
          quantity: 1,
          properties: {
            _bracelet_build_id: buildId,
            _role: 'charm',
            _slot_index: String(slotIdx + 1),
            _position: this.positionLabel(slotIdx),
          },
        })),
    ];

    // Custom length: ONE add-on line, quantity = number of steps, so a 26"
    // off an 18" chain reads as x4 rather than four separate lines.
    const addonId = parseInt(this.extraWrap?.dataset.addonVariant, 10);
    if (this.extraSteps > 0 && addonId) {
      const step = parseFloat(this.extraWrap.dataset.stepInches) || 2;
      const baseIn = Math.round((this.braceletLenMm / 25.4) - this.extraSteps * step);
      items.push({
        id: addonId,
        quantity: this.extraSteps,
        properties: {
          _bracelet_build_id: buildId,
          _role: 'length',
          _extra_inches: '+' + (this.extraSteps * step) + '"',
          ...(baseIn ? { _final_length: (baseIn + this.extraSteps * step) + '"' } : {}),
        },
      });
    }

    this.setStatus('Adding to cart…', '');

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || `Add-to-cart failed (${res.status})`);
      }
      this.setStatus('Added! Redirecting to cart…', 'success');
      clearState();
      // Give the confirmation a beat to be visible, then jump to cart
      setTimeout(() => { window.location.href = '/cart'; }, 700);
    } catch (e) {
      console.error('charm-bar add-to-cart failed:', e);
      this.setStatus(e.message || 'Something went wrong. Try again.', 'error');
      this.addBtn.disabled = false;
    }
  }

  /**
   * Human-readable text summary of the current build. Attached as a
   * line item property so admin fulfillment can read the layout in the
   * order detail view without needing to render an image.
   * Format: "Base: <chain title>\n1. <charm>\n2. <charm>\n..."
   */
  /** Human label for a spot, matching the numbers drawn on the builder. */
  positionLabel(slotIdx) {
    if (slotIdx === 0) return 'spot 1 (bottom centre)';
    const side = this.mode === 'necklace'
      ? (slotIdx % 2 === 1 ? 'left' : 'right')
      : (slotIdx % 2 === 1 ? 'right' : 'left');
    return `spot ${slotIdx + 1} (${side})`;
  }

  /**
   * The list Megan reads in the order to assemble the piece. Spots are the
   * REAL positions from the builder (1 = bottom centre, evens one side,
   * odds the other), and empty spots are stated so nothing is ambiguous.
   */
  buildTextSummary() {
    const lines = [];
    if (this.selectedBase?.title) {
      lines.push(`Base: ${this.selectedBase.title}`);
    }
    lines.push(`Charms: ${this.selectedCharms.filter(Boolean).length} of ${this.slotCount} spots`);
    this.selectedCharms.forEach((c, i) => {
      if (c?.title) lines.push(`${this.positionLabel(i)}: ${c.title}`);
    });
    return lines.join('\n');
  }


  /**
   * Snapshot the canvas (chain + placed charms) to a compact JPEG data URL.
   * Uses the native Canvas API — draws the chain image first, then each
   * filled slot's charm image at its current position + size. Skips the
   * empty attach-ring hint (not useful for admin).
   *
   * Cross-origin: Shopify's CDN sends `access-control-allow-origin: *`, so
   * setting crossOrigin='anonymous' on loaded images is enough to keep the
   * canvas exportable via toDataURL.
   *
   * Output: JPEG at ~600x800 (portrait, matches canvas 3:4), quality 0.7.
   * Typical file size: 30-80 KB base64 (~50 KB average) — well within
   * Shopify's line-item-property size tolerance.
   */
  async captureBuildImage() {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return '';

    // Smaller than you'd think — Shopify line-item properties choke on
    // huge data URLs. 320×~427 px @ 0.55 quality lands around 8-15 KB
    // base64 which cart accepts reliably. Admin can still see the layout.
    const TARGET_W = 320;
    const scale = TARGET_W / rect.width;
    const outW = TARGET_W;
    const outH = Math.round(rect.height * scale);

    const c = document.createElement('canvas');
    c.width = outW;
    c.height = outH;
    const ctx = c.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outW, outH);

    async function loadImg(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });
    }

    // Draw the chain (object-fit: contain — preserves aspect within canvas)
    if (this.selectedBase?.image) {
      try {
        const chainImg = await loadImg(this.selectedBase.image);
        const cAR = chainImg.naturalWidth / chainImg.naturalHeight;
        const outAR = outW / outH;
        let dw, dh, dx, dy;
        if (cAR > outAR) { dw = outW; dh = outW / cAR; dx = 0; dy = (outH - dh) / 2; }
        else { dh = outH; dw = outH * cAR; dy = 0; dx = (outW - dw) / 2; }
        // Mirror the on-page --cb-zoom so the chain lines up with the charm
        // rects (which come from getBoundingClientRect and already include it).
        const zoom = parseFloat(getComputedStyle(this).getPropertyValue('--cb-zoom')) || 1;
        if (zoom !== 1) {
          const org = (getComputedStyle(this).getPropertyValue('--cb-zoom-origin') || '50% 50%').trim().split(/\s+/);
          const ox = (parseFloat(org[0]) || 50) / 100 * outW;
          const oy = (parseFloat(org[1]) || 50) / 100 * outH;
          dx = ox + (dx - ox) * zoom;
          dy = oy + (dy - oy) * zoom;
          dw *= zoom;
          dh *= zoom;
        }
        ctx.drawImage(chainImg, dx, dy, dw, dh);
      } catch {}
    }

    // Draw each placed charm at its slot's current bounding rect (relative
    // to the canvas). Slot rects reflect the actual on-page layout — hole
    // positions if set, otherwise the ellipse ring.
    const canvasRect = this.canvas.getBoundingClientRect();
    for (let i = 0; i < this.slots.length; i++) {
      const charm = this.selectedCharms[i];
      if (!charm || !charm.image) continue;
      const slot = this.slots[i];
      if (!slot || slot.hidden) continue;
      const sr = slot.getBoundingClientRect();
      const sx = (sr.left - canvasRect.left) * scale;
      const sy = (sr.top - canvasRect.top) * scale;
      const sw = sr.width * scale;
      const sh = sr.height * scale;
      if (sw <= 0 || sh <= 0) continue;
      try {
        const img = await loadImg(charm.image);
        // Match the on-page hang layout: charm occupies the bottom 82% of
        // the slot, top-aligned under the jump ring (object-position: top).
        const hangY = sy + sh * 0.18;
        const hangH = sh * 0.82;
        const iAR = img.naturalWidth / img.naturalHeight;
        const slotAR = sw / hangH;
        let dw, dh, dx, dy;
        if (iAR > slotAR) { dw = sw; dh = sw / iAR; dx = sx; dy = hangY; }
        else { dh = hangH; dw = hangH * iAR; dy = hangY; dx = sx + (sw - dw) / 2; }
        ctx.drawImage(img, dx, dy, dw, dh);
      } catch {}
    }

    const dataUrl = c.toDataURL('image/jpeg', 0.55);
    // Guardrail: if the encoded URL is unusually huge (weird chain photo,
    // many charms), drop it rather than jamming the cart. Summary text
    // still gives admin enough to reconstruct the build.
    if (dataUrl.length > 60000) {
      console.warn('[charm-bar] build image too large (' + dataUrl.length + ' chars) — skipping');
      return '';
    }
    return dataUrl;
  }
}

customElements.define('charm-bar-builder', CharmBarBuilder);
